import React from "react";
import { useAppContext } from "../context/AppContext";

const PineTree = ({ x, y, scale = 1, color1 = "#4a5c3a", color2 = "#5d7049" }) => (
  <g transform={`translate(${x},${y}) scale(${scale})`}>
    <polygon points="0,-40 15,0 -15,0" fill={color1} />
    <polygon points="0,-30 12,0 -12,0" fill={color2} />
    <rect x="-2" y="0" width="4" height="12" fill="#6b5644" />
  </g>
);

const WPANationalParksBanner = () => {
  const { darkMode } = useAppContext();
  // Responsive text size and position
  const [windowWidth, setWindowWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  let fontSize = 34, textY = 200;
  if (windowWidth < 640) { // mobile
    fontSize = 20; textY = 170;
  } else if (windowWidth < 1024) { // tablet
    fontSize = 26; textY = 185;
  }

  return (
    <svg
      viewBox="0 0 1200 220"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-48 md:h-56 lg:h-64"
      role="img"
      aria-labelledby="title"
      preserveAspectRatio="xMidYMid slice"
      style={{ display: "block" }}
    >
      <title id="title">WPA-style National Parks Banner</title>
      <defs>
        {/* Sky gradients */}
        <linearGradient id="skyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={darkMode ? "#1a2236" : "#f4e4c1"} />
          <stop offset="60%" stopColor={darkMode ? "#232b43" : "#e6d5b8"} />
          <stop offset="100%" stopColor={darkMode ? "#2a314a" : "#dcc9a8"} />
        </linearGradient>
        {/* Sun/Moon gradients */}
        <linearGradient id="sunGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={darkMode ? "#e6e7ef" : "#fce38a"} />
          <stop offset="100%" stopColor={darkMode ? "#bfc3d9" : "#f7d488"} />
        </linearGradient>
        {/* Sun rays */}
        <radialGradient id="sunRays" cx="50%" cy="50%" r="50%">
          <stop
            offset="60%"
            stopColor={darkMode ? "#e6e7ef" : "#fce38a"}
            stopOpacity={darkMode ? "0.12" : "0.3"}
          />
          <stop offset="100%" stopColor={darkMode ? "#e6e7ef" : "#fce38a"} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Sky background */}
      <rect width="1200" height="220" fill="url(#skyGrad)" />

      {/* Stars */}
      {darkMode && (
        <g>
          {[...Array(40)].map((_, i) => (
            <circle
              key={i}
              cx={Math.random() * 1200}
              cy={Math.random() * 120}
              r={Math.random() * 1.5 + 0.5}
              fill="#fff"
              opacity={Math.random() * 0.5 + 0.5}
            />
          ))}
        </g>
      )}

      {/* Mountains */}
      <path
        d="M0 140 L150 100 L300 120 L450 95 L600 115 L750 90 L900 110 L1050 85 L1200 105 V220 H0Z"
        fill={darkMode ? "#3a3d4d" : "#c4b5a0"}
        opacity="0.6"
      />
      <path
        d="M0 160 L100 130 L200 145 L350 120 L500 140 L650 125 L800 145 L950 120 L1100 140 L1200 130 V220 H0Z"
        fill={darkMode ? "#5a5e6e" : "#a99177"}
      />

      {/* Sun / Moon */}
      <circle cx="1080" cy="45" r="38" fill="url(#sunGrad)" />
      <circle cx="1080" cy="45" r="80" fill="url(#sunRays)">
        <animate attributeName="r" values="70;90;70" dur="6s" repeatCount="indefinite" />
        <animate
          attributeName="opacity"
          values={darkMode ? "0.12;0.22;0.12" : "0.3;0.5;0.3"}
          dur="6s"
          repeatCount="indefinite"
        />
      </circle>

      {/* Geyser with steam */}
      <ellipse cx="980" cy="148" rx="35" ry="15" fill={darkMode ? "#5a6a7a" : "#a4ad94"} />
      <path
        d="M980 145 Q975 110 980 85 Q985 110 980 145"
        fill={darkMode ? "#bfc3d9" : "#e8e6d8"}
        opacity="0.7"
      >
        <animate attributeName="opacity" values="0.6;0.9;0.6" dur="4s" repeatCount="indefinite" />
      </path>
      <path
        d="M980 85 Q970 60 975 40 Q980 55 985 40 Q990 60 980 85"
        fill={darkMode ? "#e6e7ef" : "#f4f2e8"}
        opacity="0.5"
      >
        <animateTransform
          attributeName="transform"
          type="translate"
          values="0,0;0,-10;0,0"
          dur="5s"
          repeatCount="indefinite"
        />
      </path>

      {/* Trees */}
      {[
        { x: 100, y: 210, scale: 1 },
        { x: 150, y: 215, scale: 1.2 },
        { x: 340, y: 220, scale: 1.3 },
        { x: 860, y: 215, scale: 1.1 },
        { x: 940, y: 218, scale: 1.3 },
      ].map((props, i) => (
        <PineTree
          key={i}
          {...props}
          color1={darkMode ? "#2e3a2a" : undefined}
          color2={darkMode ? "#3d4d3a" : undefined}
        />
      ))}

      {/* Heading text, centered */}
      <text
        x="600"
        y={textY}
        textAnchor="middle"
        fontFamily="'Rye', 'Oswald', 'Arial Black', 'Arial', sans-serif"
        fontWeight="bold"
        fontSize={fontSize}
        fill={darkMode ? "#e6e7ef" : "#111111"}
        letterSpacing="0.12em"
        style={{
          textTransform: 'uppercase',
          filter: darkMode
            ? 'drop-shadow(2px 4px 0 #000) drop-shadow(0 2px 8px #0008)'
            : 'drop-shadow(2px 4px 0 #fff) drop-shadow(0 2px 8px #0001)',
        }}
      >
        Gear & Equipment
      </text>
      {/* River shimmer removed */}
    </svg>
  );
};

export default WPANationalParksBanner;
